// Angular
import { Injectable, inject } from '@angular/core';
// Sentry
import * as Sentry from '@sentry/angular';
// Environment
import { environment } from '@environment';
// Lodash
import { noop } from 'lodash';
// Service
import { GlobalErrorHandler } from './global-error-handler.service';
// Model
import { ISentryUserIdentity } from '@shared/models';
// Package.JSON
import packageInfo from '@packageInfo';

@Injectable({
    providedIn: 'root'
})
export class SentryService {
    private readonly globalErrorHandler = inject(GlobalErrorHandler);

    constructor() {
        environment.isSentryLoggingEnable ? this.initSentry() : noop();
    }

    /**
     * Initialise Sentry logging
     */
    public initSentry(): void {
        Sentry.init({
            dsn: environment.sentryDsn,
            maxValueLength: 500,
            environment: environment.production ? 'PROD' : 'DEV',
            release: `${packageInfo.name}-v${packageInfo.version}`,

            ignoreErrors: [
                'Timeout',
                'Non-Error exception captured',
                '0 Unknown Error',
                'ChunkLoadError',
                'InvalidStateError',
                'Error: NG02100',
                'Object.prototype.hasOwnProperty.call(e,"telephone")',
                "TypeError: 'text/html' is not a valid JavaScript MIME type",
                'Invalid call to runtime.sendMessage()',
                'Old view transition aborted by new view transition',
                "Identifier 'change_ua' has already been declared",
                'Non-Error promise rejection captured with value: undefined',
                'TypeError: Failed to fetch dynamically imported module',
                'TypeError: error loading dynamically imported module',
                'InvalidStateError: Skipping view transition because viewport size changed.',
                'View transition was skipped because document visibility state is hidden',
                'Skipping view transition because viewport size changed',
                'Skipping view transition because document visibility state has become hidden',
                "Unexpected token 'else'",
                'Load failed',
                'Skipping view transition because skipTransition() was called.',
                'Java exception was raised during method invocation',
                "Can't find variable: disableWebkitEnterFullscreen",
                "TypeError: undefined is not an object (evaluating 'a.K')",
                'Unexpected end of input',
                "Can't find variable: gmo",
                'illegal access',
                'Maximum call stack size exceeded',
                'SyntaxError: Unexpected end of input',
                'SyntaxError: missing ) after argument list',
                /^Unexpected token/i,
                /^uncaught exception/i,
            ],

            beforeSend(event) {
                const errorMessage =
                    event.exception?.values?.[0]?.value ||
                    event.message ||
                    event.logentry?.message ||
                    '';

                const ignoredErrors: Array<string | RegExp> = [
                    'ChunkLoadError',
                    'TypeError: Failed to fetch dynamically imported module',
                    'TypeError: error loading dynamically imported module',
                    "TypeError: 'text/html' is not a valid JavaScript MIME type",
                ];

                if (
                    errorMessage &&
                    ignoredErrors.some((rule) =>
                        typeof rule === 'string' ? errorMessage.includes(rule) : rule.test(errorMessage)
                    )
                ) {
                    return null;
                }

                return event;
            },
        });
    }

    /**
     * Set user identity information to Sentry
     */
    public setUser(payload: ISentryUserIdentity): void {
        const scope = Sentry.getCurrentScope();
        scope.setUser(payload);
    }

    /**
     * Set tags to Sentry issue
     */
    public setTag(key: string, value: string): void {
        const scope = Sentry.getCurrentScope();
        scope.setTag(key, value);
    }

    /**
     * Reset Sentry config when user logged out
     */
    public resetAll(): void {
        const scope = Sentry.getCurrentScope();
        scope.setUser({ email: '' });
    }

    /**
     * Open Sentry report dialog modal
     */
    public openFeedbackDialog(user: any): void {
        try {
            const eventId: string = Sentry.captureMessage(
                `${user.id} | ${user.name} | ${user.email} | Reported Technical issue`
            );

            Sentry.showReportDialog({
                eventId,
                title: 'Report Technical Issue',
                subtitle: 'Our team will be notified. If you would like to help, tell us what happened below.',
                subtitle2: '',
                labelSubmit: 'Report Issue',
                user: {
                    email: user.email,
                    name: user.name
                }
            });
        } catch (error) {
            this.globalErrorHandler.handleError(error);
        }
    }
}